import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import type {
  ID,
  LayoutPresetId,
  Rect,
  Region,
  SourceConfig,
} from "@gooner/shared";
import type { SyncMode } from "@gooner/shared";
import {
  adaptiveFramingOn,
  autoOptimizeActive,
  autoOptimizeOverrides,
  facetLabel,
  keywordGroups,
  MONITOR_TEMPLATES,
  makeId,
  regionFromRect,
  relayoutRegions,
  syncLeaderOf,
  VIEW_NAME_MAX,
} from "@gooner/shared";
import { api } from "../../lib/api";
import { useSession } from "../../lib/session";
import { clampRect } from "../lib/regionGeometry";
import { useCollectionOptions, useContentTags } from "../lib/feedData";
import { regionPacingLabel, sourceIsUnset, sourceLabel } from "../lib/feedLabels";
import { linkRegions, toggleMirror, unlinkAdoptShared } from "../lib/regionSync";
import { Select, TextInput, Toggle, cx } from "../ui/primitives";
import { TagChip } from "./TagChips";
import { hintTitle } from "./hintTitle";
import { FeedPicker } from "./FeedPicker";
import { RegionLinkButton } from "./RegionLinkButton";
import { RegionMirrorButton } from "./RegionMirrorButton";
import {
  IconCopy,
  IconPlus,
  IconToBack,
  IconToFront,
  IconTrash,
} from "../ui/icons";
import { track } from "../../analytics";
import { regionSourceCollectionProps } from "../../lib/viewEvents";
import { facetCombo } from "../../lib/viewShape";
import { SourcePreview } from "./SourcePreview";
import { PresetPalette } from "./PresetPalette";
import { RegionCanvas } from "./RegionCanvas";
import { AuthModal } from "./AuthModal";
import { ViewRowList } from "./ViewRowList";
import { ConfirmDialog } from "./ConfirmDialog";
import { Masked, maskIf } from "../../lib/replayMask";
import { type LayoutChoice,
  choiceLabel,
  choiceRegionCount,
  needsConfirm,
  } from "../lib/layoutChoice";
import { type GoldGate, trackGoldGate, useGoldGate } from "../../lib/gold";
import { GoldDialog } from "./GoldDialog";
import { type CollectionOption } from "../lib/monitorTemplates";
import { ASPECT_PRESETS, aspectKey } from "../lib/format";
import type { View } from "@gooner/shared";

/** What the editor edits: anything with a name, an aspect and regions. A View and a
 *  Monitor both satisfy this, which is what lets one editor serve the view editor and
 *  the cave editor while caves are being retired. */
export interface EditorSubject {
  id: string;
  name: string;
  /** Emoji shown on the subject's card; absent means the default glyph. */
  icon?: string;
  aspect: { w: number; h: number };
  /** Whole-view switch: screens transpose the layout when their orientation
   *  disagrees with `aspect`. The editor canvas always shows the authored layout. */
  adaptiveLayout?: boolean;
  regions: Region[];
}

/* Data helpers (useCollectionOptions / useContentTags) live in ../lib/feedData, and the
   region label vocabulary (sourceLabel / regionPacingLabel / sourceIsUnset) in
   ../lib/feedLabels — shared with the screen's hover chrome and region popup, so the
   editor and the wall can never describe the same region differently. */

/** Quick picks for the view's card emoji — the vocabulary the templates already use,
 *  so the row offers glyphs the rest of the app renders well. The picker covers the rest. */
const VIEW_EMOJI_CHOICES: string[] = [
  ...new Set(["🖥️", ...MONITOR_TEMPLATES.map((t) => t.emoji)]),
].slice(0, 12);

/** Split into user-perceived glyphs, not code points: a plain string spread cuts
 *  ❤️-style emoji (base + variation selector) in two, one half invisible. */
const graphemes = (s: string): string[] =>
  [...new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(s)].map(
    (x) => x.segment,
  );

/** The full picker behind the "more" button at the end of the quick-pick row.
 *  Hand-rolled like every popover here — no picker dependency for one emoji field. */
const EMOJI_PICKER_GROUPS: { label: string; emoji: string[] }[] = [
  { label: "Mood", emoji: graphemes("😈😏🥵😍🤤😜🙈😳🥰😘🤪😎🔞💀👀") },
  { label: "Body", emoji: graphemes("🍑🍒🍈🍆🌮💦👅👄💋🦵👙👠🩲🧦") },
  { label: "Hearts", emoji: graphemes("❤️🧡💛💚💙💜🖤🤍💕💖💘💝💟❣️") },
  { label: "Heat", emoji: graphemes("🔥⚡✨💫🌟⭐🌈🌙☀️🌊❄️🎆") },
  { label: "Play", emoji: graphemes("🎬🎥📺📼🎮🕹️🎲🎰🎯🎪🎭🎨🎧🎵") },
  { label: "Things", emoji: graphemes("👑💎💰💵🏆🥇🔒🔑🔔💣🧲🪝⛓️🕯️") },
  { label: "Creatures", emoji: graphemes("🐱🐰🦊🐍🦋🕷️🐙🦄🐺🐯🦁🐻") },
  { label: "Food", emoji: graphemes("🍓🍌🍭🍯🍦🍰🧁🍫🍾🍷🍸🥛☕🧃") },
];

/* -------------------------------------------------------------------------- */
/* The simple editor                                                          */
/* -------------------------------------------------------------------------- */

export function SimpleEditor({
  subject,
  onRegions,
  onAspect,
  onAdaptiveLayout,
  onRenameView,
  onSetIcon,
  header,
  overlay,
}: {
  subject: EditorSubject;
  onRegions: (regions: Region[]) => void;
  onAspect: (aspect: { w: number; h: number }) => void;
  onAdaptiveLayout?: (on: boolean) => void;
  /** Rename the subject from the view panel (shown while no region is selected).
   *  Optional: a host with its own identity chrome and no view panel omits it. */
  onRenameView?: (name: string) => void;
  /** Set (or clear, with undefined) the subject's card emoji from the view panel. */
  onSetIcon?: (icon: string | undefined) => void;
  /** Whatever identity chrome the caller wants above the canvas, or nothing for a
   *  subject that has none. Region editing itself never needs to know what's in here. */
  header?: ReactNode;
  /** Modals and the like, rendered outside the editor's grid so nothing in it can clip
   *  or re-lay-out them. The caller owns whether one is showing. */
  overlay?: ReactNode;
}) {
  const navigate = useNavigate();
  const { collections, loading: collectionsLoading } = useCollectionOptions();
  const { tags: contentTags, loading: tagsLoading } = useContentTags();
  const user = useSession((s) => s.user);

  // Region selection is editor-local UI state, not part of the subject: it used to
  // live in editorStore, which is cave-shaped, but a view has no cave to hang it off
  // of. It lives here instead, and resets whenever the subject itself changes.
  //
  // Nothing starts selected: no selection is a real state — the left panel shows the
  // subject's own settings there — so a region is only ever selected by an act that
  // names one (a click, or creating it).
  const [selectedRegionId, setSelectedRegionId] = useState<ID | null>(null);

  const selectRegion = (id: ID | null) => setSelectedRegionId(id);

  // ---- region mutations, computed from subject.regions and pushed up via onRegions;
  // these replace the editorStore actions that used to do the same thing scoped to
  // the active monitor. ----
  /**
   * True (and reported, and the modal opened) when growing the canvas by one more
   * region would pass the tier ceiling. Every path that ADDS a region asks first —
   * the Add Region tool AND duplicateRegion, which builds its copy without
   * addRegionRect and was the leak an earlier draft of this gate had. The
   * control stays clickable on purpose: a disabled button that never reports is
   * invisible frustration, and gate_hit is half the funnel.
   */
  const regionGateHit = (): boolean => {
    if (subject.regions.length < maxRegions) return false;
    trackGoldGate("regions");
    setGoldGate("regions");
    return true;
  };

  const addRegionRect = (rect: Rect): ID | null => {
    if (regionGateHit()) return null;
    const region = regionFromRect(clampRect(rect));
    onRegions([...subject.regions, region]);
    selectRegion(region.id);
    return region.id;
  };

  const updateRegionRect = (id: ID, rect: Rect) => {
    const c = clampRect(rect);
    onRegions(subject.regions.map((r) => (r.id === id ? { ...r, ...c } : r)));
  };

  const duplicateRegion = (id: ID) => {
    if (regionGateHit()) return;
    const src = subject.regions.find((r) => r.id === id);
    if (!src) return;
    // Offset the copy so it doesn't sit invisibly under the original.
    const rect = clampRect({
      x: src.x + 0.02,
      y: src.y + 0.02,
      w: src.w,
      h: src.h,
    });
    const copy: Region = {
      ...src,
      ...rect,
      id: makeId(),
      source: { ...src.source },
    };
    onRegions([...subject.regions, copy]);
    selectRegion(copy.id);
  };

  const deleteRegion = (id: ID) => {
    // Free anything that followed the deleted region: a dangling syncWith renders as
    // unsynced on the wall but leaves the panel claiming a sync with nothing to show.
    const remaining = subject.regions
      .filter((r) => r.id !== id)
      .map((r) =>
        r.syncWith === id ? { ...r, syncWith: undefined, syncMode: undefined } : r,
      );
    onRegions(remaining);
    // Deleting the selected region leaves nothing selected — the panel falls
    // back to the view's own settings rather than jumping to a sibling.
    if (selectedRegionId === id) setSelectedRegionId(null);
  };

  const renameRegion = (id: ID, name: string) => {
    onRegions(subject.regions.map((r) => (r.id === id ? { ...r, name } : r)));
  };

  /** Move a region to the end (front) or start (back) of the paint order.
   *  Paint order IS array order — the display and the canvas both draw the
   *  region list in sequence, so this persists through the ordinary region
   *  save and needs no field of its own. */
  const reorderRegion = (id: ID, place: "front" | "back") => {
    const r = subject.regions.find((x) => x.id === id);
    if (!r) return;
    const rest = subject.regions.filter((x) => x.id !== id);
    track("region_reordered", { place });
    onRegions(place === "front" ? [...rest, r] : [r, ...rest]);
  };

  const setSourceForRegions = (ids: ID[], source: SourceConfig) => {
    const idSet = new Set(ids);
    onRegions(
      subject.regions.map((r) => (idSet.has(r.id) ? { ...r, source } : r)),
    );
  };

  /* Both sync mutations are the shared transforms in ../lib/regionSync — the screen's
     hover chrome reshapes regions through the same two functions, so what linking does
     is defined once. Only where the new array GOES differs (onRegions here, a PUT there).
     The panel's Unsync-restores-the-snapshot behaviour is linkRegions with null. */
  const setSyncForRegion = (id: ID, syncWith: ID | null, mode: SyncMode) => {
    onRegions(linkRegions(subject.regions, id, syncWith, mode));
  };

  const unlinkRegion = (id: ID) => {
    onRegions(unlinkAdoptShared(subject.regions, id));
  };

  /**
   * Written both ways round, never left absent to mean "on".
   *
   * Absence is the DEFAULT (see `Region.autoOptimize`), and it is the right stored shape
   * for a region nobody has touched. But once somebody has turned this on deliberately,
   * `true` is what says so — and a region that stored absence for "on" would silently
   * follow the default if it ever changed.
   */
  const setAutoOptimizeForRegions = (ids: ID[], autoOptimize: boolean) => {
    const idSet = new Set(ids);
    onRegions(
      subject.regions.map((r) => (idSet.has(r.id) ? { ...r, autoOptimize } : r)),
    );
  };

  /** Written both ways round, for the reason spelled out above setAutoOptimizeForRegions. */
  const setAdaptiveFramingForRegions = (ids: ID[], adaptiveFraming: boolean) => {
    const idSet = new Set(ids);
    onRegions(
      subject.regions.map((r) => (idSet.has(r.id) ? { ...r, adaptiveFraming } : r)),
    );
  };

  const [saved, setSaved] = useState<View[]>([]);
  /**
   * The view a delete is being confirmed for.
   *
   * It destroys a row in the user's library — the list under Layouts is their views, not
   * a separate collection of shapes — so it cannot be a single unconfirmed click, and it
   * may not describe what it deletes as anything smaller than a view.
   */
  const [pendingDelete, setPendingDelete] = useState<View | null>(null);

  /** Non-null while the sign-up modal is open; the string says why. */
  const [signUpReason, setSignUpReason] = useState<string | null>(null);
  // The Gold region/layout gate — same channel shape as signUpReason above: the gated
  // click sets it, the modal renders from it, closing clears it.
  const [goldGate, setGoldGate] = useState<GoldGate | null>(null);
  const { maxRegions } = useGoldGate();

  /**
   * A layout choice waiting on a confirm, held as the thunk that applies it.
   *
   * Same pattern as `pendingDelete` above, and for the same reason: the selector serves
   * an empty view and a populated one from one control, so a click that is harmless on
   * the create path is destructive on the change path, and only the second may ask.
   *
   * The thunk closes over the render that created it, so `applyPresetAndShow` relayouts
   * `subject.regions` as they were when the choice was made rather than when it was
   * confirmed. Deliberate and harmless here: the dialog is modal, and those regions are
   * the thing being replaced. It would stop being harmless if this ever held a choice
   * across an edit the user could make in between.
   */
  const [pendingLayout, setPendingLayout] = useState<{
    label: string;
    /** The label is a saved view's name (somebody's words), not a preset's. */
    user: boolean;
    apply: () => void;
  } | null>(null);

  /** Apply a layout choice, asking first when it would destroy work on screen —
   *  unless the choice's region count is past the tier ceiling, in which case the
   *  answer is the upsell, not the confirm. */
  const chooseLayout = (choice: LayoutChoice, apply: () => void) => {
    if (choiceRegionCount(choice) > maxRegions) {
      trackGoldGate("layout");
      setGoldGate("layout");
      return;
    }
    if (needsConfirm(choice, subject.regions.length)) {
      setPendingLayout({ label: choiceLabel(choice), user: choice.kind === "saved", apply });
      return;
    }
    apply();
  };

  // Collections belong to an account, so an anonymous user has to make one first.
  const createCollection = () => {
    if (user) navigate("/collections");
    else
      setSignUpReason(
        "Collections live on your account — make one to start building your own.",
      );
  };

  const VIEWS_SIGNUP_REASON =
    "Views live on your account, so they follow you to every screen you sign in on.";

  // Shared by the full selector and the View panel's embedded mini grid, so a preset
  // picked in either place walks the same gate: Gold ceiling, then confirm, then apply.
  // Tracked inside the apply callback, after any confirm dialog: a pick someone backed
  // out of is not a pick.
  const pickPreset = (id: LayoutPresetId) =>
    chooseLayout({ kind: "preset", id }, () => {
      track("layout_picked", { layout: id });
      applyPresetAndShow(id);
    });
  const lockedLayout = () => {
    trackGoldGate("layout");
    setGoldGate("layout");
  };

  // A freshly selected subject lands on its canvas — empty or not — with no region
  // selected, so the panel opens on the subject's own settings. Same rule as the
  // initial state above, because switching monitors in the cave editor is the same
  // event as opening one.
  useEffect(() => {
    setSelectedRegionId(null);
    // Deliberately keyed on subject.id alone: re-running this whenever
    // subject.regions changes would fight every region edit's own selection update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject.id]);

  // Clicking anywhere that isn't the canvas or the region panel drops the selection,
  // returning the panel to the subject's own settings. The canvas handles its own
  // deselect (pointerdown on empty surface); the panel must never deselect — it IS
  // the selection's controls. Suspended while any modal is up: Cancel on a confirm,
  // or a click into a portaled auth/Gold dialog, is an answer to that dialog, not a
  // statement about the selection.
  const canvasSectionRef = useRef<HTMLElement | null>(null);
  const panelSectionRef = useRef<HTMLElement | null>(null);
  const modalUp =
    !!signUpReason || !!goldGate || !!pendingDelete || !!pendingLayout || overlay != null;
  useEffect(() => {
    if (selectedRegionId === null || modalUp) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (canvasSectionRef.current?.contains(t)) return;
      if (panelSectionRef.current?.contains(t)) return;
      selectRegion(null);
    };
    // Escape too — but not while typing, where Escape means "revert this field".
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const el = document.activeElement;
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)
        return;
      selectRegion(null);
    };
    document.addEventListener("pointerdown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      window.removeEventListener("keydown", onKey);
    };
    // selectRegion is recreated per render; keying on it would re-subscribe every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegionId, modalUp]);

  const regions = subject.regions;
  const selectedRegion = regions.find((r) => r.id === selectedRegionId) ?? null;

  /*
   * The account's other views, read straight from /views.
   *
   * These were called "presets" and reached through a /layouts API that read and wrote
   * the same rows under a second set of names — SavedLayout was a type alias for View,
   * and createSavedLayout called createView. The alias layer went first; the word
   * outlived it by a while, which is how the editor came to offer "presets" and "views"
   * as if they were two things a user had to choose between. They were always one list.
   *
   * Note the word still means something else nearby, and that meaning is untouched: a
   * LAYOUT preset is a bare geometry (Single, Grid 2×2 — see LAYOUT_PRESETS), which is
   * what `PresetPalette` above offers and what `kind: "preset"` selects.
   *
   * Saving requires an account, so a signed-out visitor simply has none.
   */
  const refreshSaved = () => {
    if (!user) {
      setSaved([]);
      return Promise.resolve();
    }
    return api.views
      .list()
      .then((r) => setSaved(r.views))
      .catch(() => setSaved([]));
  };
  useEffect(() => {
    void refreshSaved();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const applyPresetAndShow = (id: LayoutPresetId) => {
    // Preserve existing feeds — re-geometry the current regions onto the new
    // layout instead of resetting them all back to random.
    const next = relayoutRegions(subject.regions, id);
    onRegions(next);
    // A layout choice names no region, so none is selected: the panel lands on the
    // subject's settings. (Also clears ids that no longer exist after the swap.)
    selectRegion(null);
  };

  const loadSaved = (l: View) => {
    // Fresh ids: copying one view into two subjects would otherwise give both
    // the same region ids, and region ids are the handle the editor selects by.
    // syncWith is re-pointed through the same map — the exact normalization
    // builtinView does after renaming, for the exact same reason: fresh ids with
    // stale follows silently break every sync link in the copy. A follow aimed
    // outside the copied view was already dangling and is dropped.
    const idMap = new Map(l.regions.map((r) => [r.id, makeId()]));
    const next = l.regions.map((r) => {
      const syncWith = r.syncWith ? idMap.get(r.syncWith) : undefined;
      return { ...r, id: idMap.get(r.id)!, syncWith, syncMode: syncWith ? r.syncMode : undefined };
    });
    onRegions(next);
    selectRegion(null);
  };

  /** Blank region dropped in the middle of the subject.
   *  First region fills the wall (size=1, same geometry as the `single` layout cell) —
   *  Casey AM 2026-09-25: empty-view Add Region → Play showed a postage-stamp of video
   *  in a sea of black when the default was always quarter-size. Subsequent Add Region
   *  stays quarter-size so extras can sit beside existing ones. */
  const addCenterRegion = () => {
    const size = regions.length === 0 ? 1 : 0.25;
    addRegionRect({ x: (1 - size) / 2, y: (1 - size) / 2, w: size, h: size });
  };

  /** Where region-panel edits aim: the selected region, as a list because
   *  effectiveTargetIds routes through sync leaders and is shaped for many. */
  const targetIds = selectedRegionId ? [selectedRegionId] : [];

  /** Where an edit LANDS: a follower's edits go to the shared config on its leader —
   *  sync is one configuration with two tiles, whichever tile was selected. Set-deduped,
   *  because a selection can hold a follower AND its leader. */
  const effectiveTargetIds = (ids: ID[]): ID[] => {
    const out = new Set<ID>();
    for (const id of ids) {
      const r = regions.find((x) => x.id === id);
      if (r) out.add(syncLeaderOf(r, regions)?.id ?? id);
    }
    return [...out];
  };

  const setSource = (s: SourceConfig) => {
    if (!targetIds.length) return;
    const landed = effectiveTargetIds(targetIds);
    // One event per PICK, not per tile it lands on — the act is one decision. Keyword
    // picks carry the facet combo (free text counted, never sent — facetCombo's rule);
    // a collection pick never carries a title: a built-in collection (Editor's Choice,
    // the reserved faps/uploads) sends its stable `collection_slug`, and every other one
    // — public, somebody else's, your own — only a pseudonymous `collection_id`, because
    // a collection's title is the words of whoever made it (lib/viewEvents.ts).
    track("region_source_set", {
      kind: s.kind,
      regions: landed.length,
      ...(s.kind === "keywords" ? facetCombo(keywordGroups(s).flat()) : {}),
      ...regionSourceCollectionProps(s, collections),
    });
    setSourceForRegions(landed, s);
  };

  return (
    // Mobile: one column, sections in reading order — the canvas sits above the
    // tile panel so you pick a tile and then say what it plays. Desktop (lg+):
    // the two sections share a row, panel left and canvas right.
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-5 p-4 lg:grid lg:max-w-6xl lg:grid-cols-2 lg:items-start lg:gap-x-8 lg:gap-y-6">
      {/* Whatever identity chrome the caller wants above the canvas, or nothing for
          a subject that has none. This component never looks inside it. */}
      {header ? (
        <section className="lg:col-span-2 lg:col-start-1 lg:row-start-1">
          {header}
        </section>
      ) : null}

      {/* Layout — first in the DOM so mobile stacks it above the region panel; desktop
          places it explicitly in the right column. The slot always holds the canvas:
          an empty view is an empty canvas, and the View panel's preset grid (plus
          drag-to-draw) is how it stops being empty. The full selector stage that used
          to fill this slot is gone with the Change-layout buttons that reached it. */}
      <section ref={canvasSectionRef} className="lg:col-start-2 lg:row-start-2">
        <SectionLabel>Layout</SectionLabel>
        <RegionCanvas
              monitor={subject}
              selectedId={selectedRegionId}
              onSelect={selectRegion}
              onChangeRect={updateRegionRect}
              onDeleteRegion={deleteRegion}
              describeRegion={(r) => {
                // A follower's tile tells the truth: the leader's feed — not the
                // ignored source it keeps as an unsync snapshot. No "Synced:"
                // prefix; the badge's group colour already says it's linked.
                const leader = syncLeaderOf(r, regions);
                const shown = leader ?? r;
                return {
                  feed: sourceLabel(shown.source, collections),
                  // Followers get no pacing pill — the badge's group colour says
                  // "linked" now, and the leader's pill already reads the band.
                  timer: leader ? null : regionPacingLabel(r),
                  unset: sourceIsUnset(shown.source),
                };
              }}
              renderPreview={(r) => {
                const leader = syncLeaderOf(r, regions);
                // Always muted: a dozen tiles unmuted at once is unusable.
                const preview = (
                  <SourcePreview source={(leader ?? r).source} fit={r.fit} muted compact />
                );
                return leader && r.syncMode === "flip" ? (
                  <div className="absolute inset-0" style={{ transform: "scaleX(-1)" }}>
                    {preview}
                  </div>
                ) : (
                  preview
                );
              }}
              renderTileAction={(r) => (
                <div className="flex items-center gap-1">
                  {/* Mirror first so the link button keeps its fixed corner spot —
                      the mirror only exists while synced and would otherwise shove
                      the one always-there control around as syncs come and go. */}
                  <RegionMirrorButton
                    region={r}
                    allRegions={regions}
                    onToggle={() => onRegions(toggleMirror(subject.regions, r.id))}
                  />
                  <RegionLinkButton
                    region={r}
                    allRegions={regions}
                    onLink={(target) => setSyncForRegion(r.id, target, "normal")}
                    onUnlink={() => unlinkRegion(r.id)}
                  />
                </div>
              )}
            />
            <div className="relative mt-2 flex items-center justify-center gap-1.5">
              {/* Layout changes live in the View panel's embedded preset grid. */}
              <CanvasTool label="Add Region" onClick={addCenterRegion}>
                <IconPlus />
              </CanvasTool>
              <CanvasTool
                label="Copy Region"
                disabled={!selectedRegion}
                onClick={() =>
                  selectedRegion && duplicateRegion(selectedRegion.id)
                }
              >
                <IconCopy />
              </CanvasTool>
              {/* Only meaningful when regions overlap, but always shown: hiding a
                  control the moment it's useless makes the toolbar shape-shift. */}
              <CanvasTool
                label="To Front"
                disabled={
                  !selectedRegion ||
                  regions[regions.length - 1]?.id === selectedRegion.id
                }
                onClick={() =>
                  selectedRegion && reorderRegion(selectedRegion.id, "front")
                }
              >
                <IconToFront />
              </CanvasTool>
              <CanvasTool
                label="To Back"
                disabled={
                  !selectedRegion || regions[0]?.id === selectedRegion.id
                }
                onClick={() =>
                  selectedRegion && reorderRegion(selectedRegion.id, "back")
                }
              >
                <IconToBack />
              </CanvasTool>
              <CanvasTool
                label="Delete Region"
                danger
                disabled={!selectedRegion}
                onClick={() =>
                  selectedRegion && deleteRegion(selectedRegion.id)
                }
              >
                <IconTrash />
              </CanvasTool>

            </div>
            {/* Two hints, one shown: a finger has no handles to speak of — it grabs the
                selected tile's edges (RegionCanvas) — so telling it about handles sends
                it hunting for 10px squares. Coarse swaps the line; a context that reports
                neither keeps the mouse one, the usual polarity. */}
            <p className="mt-2 text-center text-[11px] leading-relaxed text-zinc-500">
              <span className="pointer-coarse:hidden">
                Drag a region to move · drag handles to resize
              </span>
              <span className="hidden pointer-coarse:inline">
                Tap a tile, then drag its edges to resize · drag its middle to move
              </span>
            </p>

            {/* Your other views, under the canvas rather than only under the picker.
                Reaching them used to mean pressing `Change layout` — a control whose
                whole job is to replace what you are working on — so the safe action
                (go open another view) was hidden behind the destructive-looking one. */}
            <div className="mt-6">
              <YourViews
                saved={saved.filter((v) => v.id !== subject.id)}
                user={user}
                onOpenSaved={(l) => navigate(`/views/${l.id}`)}
                // Same confirm-then-replace path the picker uses. Copying here is if
                // anything MORE destructive — there is a laid-out canvas to lose — so it
                // must not become a quieter action just because it moved.
                onSaved={(l) =>
                  chooseLayout({ kind: "saved", layout: l }, () => {
                    // A saved layout's name is the user's own words, so it travels
                    // as the literal "saved" — tracked after any confirm, so a
                    // backed-out copy is not a pick.
                    track("layout_picked", { layout: "saved" });
                    loadSaved(l);
                  })
                }
                onDeleteSaved={(l) => setPendingDelete(l)}
                onSignUp={() => setSignUpReason(VIEWS_SIGNUP_REASON)}
              />
            </div>
      </section>

      {/* Region or View — everything about the selected region while one is selected;
          the subject's own settings (name, emoji, layout, aspect, adaptive) while none
          is. No selection is a real state, not a disabled one. */}
      <section ref={panelSectionRef} className="lg:col-start-1 lg:row-start-2">
        <SectionLabel>{selectedRegion ? "Region" : "View"}</SectionLabel>
        {selectedRegion ? (
          <FeedPalette
            collections={collections}
            tags={contentTags}
            loading={collectionsLoading || tagsLoading}
            selected={selectedRegion}
            index={regions.findIndex((r) => r.id === selectedRegion.id)}
            onSetSource={setSource}
            onSetAutoOptimize={(on) =>
              targetIds.length && setAutoOptimizeForRegions(effectiveTargetIds(targetIds), on)
            }
            onSetAdaptiveFraming={(on) =>
              targetIds.length && setAdaptiveFramingForRegions(effectiveTargetIds(targetIds), on)
            }
            onRename={(name) => renameRegion(selectedRegion.id, name)}
            onCreateCollection={createCollection}
            // Sync is a relationship between two regions, so it needs the others.
            allRegions={regions}
            onSetSync={(target, mode) =>
              setSyncForRegion(selectedRegion.id, target, mode)
            }
          />
        ) : (
          <ViewSettingsPanel
            subject={subject}
            onRenameView={onRenameView}
            onSetIcon={onSetIcon}
            onAspect={onAspect}
            onAdaptiveLayout={onAdaptiveLayout}
            onPreset={pickPreset}
            onLockedLayout={lockedLayout}
            maxRegions={maxRegions}
          />
        )}
      </section>

      {overlay}

      {signUpReason ? (
        <AuthModal
          initialMode="register"
          reason={signUpReason}
          onClose={() => setSignUpReason(null)}
        />
      ) : null}

      {goldGate ? <GoldDialog source={goldGate} onClose={() => setGoldGate(null)} /> : null}

      {/* The row list's trash destroys a row in the library, not a local copy of a
          shape — so the wording is the library's. It is the same view that would
          disappear from the home screen. */}
      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete this saved view?"
        message={
          pendingDelete ? (
            // The view's name is the person's: masked in replay, the warning stays.
            <>“<Masked>{pendingDelete.name}</Masked>” will be permanently removed from your library, and from every screen showing it. This cannot be undone.</>
          ) : (
            ""
          )
        }
        onConfirm={() => {
          const doomed = pendingDelete;
          setPendingDelete(null);
          if (doomed) void api.views.remove(doomed.id).then(refreshSaved);
        }}
        onCancel={() => setPendingDelete(null)}
      />

      {/* The other thing a click in the selector can destroy. Not the same dialog as
          the one above: that one deletes a row from the library, this one throws away
          the arrangement on screen and keeps the view. */}
      <ConfirmDialog
        open={!!pendingLayout}
        title="Replace this layout?"
        message={
          pendingLayout ? (
            // A layout preset's label is ours; a saved view's is the person's (masked).
            <>Applying “<span {...maskIf(pendingLayout.user)}>{pendingLayout.label}</span>” discards the regions you have placed and what each of them plays. The view itself is kept.</>
          ) : (
            ""
          )
        }
        confirmLabel="Replace"
        onConfirm={() => {
          const choice = pendingLayout;
          setPendingLayout(null);
          choice?.apply();
        }}
        onCancel={() => setPendingLayout(null)}
      />
    </div>
  );
}

/**
 * Your other views — the list, the empty state, and the signed-out prompt.
 *
 * Lives under the canvas. It once lived only inside the (since-removed) layout-selector
 * stage — a MODE rather than a panel — so choosing a layout swapped the selector out and
 * took the list with it.
 *
 * Signed out, this is a sign-up prompt rather than an empty list, and that is honest
 * rather than pushy: saving a view requires an account (POST /views checks
 * isAccountOwner), so a signed-out visitor has nowhere for a view to have come from. An
 * empty list would imply they had none yet; the prompt says why there can't be any.
 */
function YourViews({
  saved,
  user,
  onOpenSaved,
  onSaved,
  onDeleteSaved,
  onSignUp,
}: {
  saved: View[];
  user: unknown;
  onOpenSaved: (l: View) => void;
  onSaved: (l: View) => void;
  onDeleteSaved: (l: View) => void;
  onSignUp: () => void;
}) {
  return (
    <div>
      <SectionLabel>Your views</SectionLabel>
      {!user ? (
        <button
          type="button"
          onClick={onSignUp}
          className="w-full rounded-xl border border-dashed border-[var(--color-edge)] p-4 text-left text-xs text-zinc-500 transition-colors hover:border-[var(--color-accent)]/60 hover:text-zinc-300"
        >
          Sign up to keep your views and re-use them here.
        </button>
      ) : saved.length === 0 ? (
        <p className="rounded-xl border border-dashed border-[var(--color-edge)] p-4 text-xs text-zinc-500">
          No other views yet. The ones you save show up here, to open or to copy
          into this one.
        </p>
      ) : (
        <ViewRowList
          views={saved}
          onOpen={onOpenSaved}
          onCopy={onSaved}
          onDelete={onDeleteSaved}
        />
      )}
    </div>
  );
}

/**
 * Everything about the region you have selected on the canvas: its name, what it
 * plays, how long each clip runs. (Not whether it is muted: sound is the screen's,
 * not the region's — see the screen's bar.) The inspector for whichever region is
 * active; the View panel is its view-level sibling.
 *
 * A collection is a single-value source so it replaces the feed; keywords accumulate.
 */
function FeedPalette({
  collections,
  tags,
  loading = false,
  selected,
  index,
  onSetSource,
  onSetAutoOptimize,
  onSetAdaptiveFraming,
  onRename,
  onCreateCollection,
  allRegions,
  onSetSync,
}: {
  collections: CollectionOption[];
  tags: string[];
  /**
   * Both chip lists are still being fetched. Without this the palette shows an empty
   * Collections row and no facet groups, which is exactly what it shows when the catalog
   * genuinely has nothing — so a slow fetch is indistinguishable from an empty library.
   */
  loading?: boolean;
  selected: Region;
  index: number;
  /** How many regions the edits below will touch. */
  onSetSource: (s: SourceConfig) => void;
  onSetAutoOptimize: (on: boolean) => void;
  onSetAdaptiveFraming: (on: boolean) => void;
  onRename: (name: string) => void;
  onCreateCollection: () => void;
  /** Every region on the canvas — sync is a relationship, so it needs the candidates. */
  allRegions?: Region[];
  onSetSync?: (target: ID | null, mode: SyncMode) => void;
}) {
  // The config this panel SHOWS and EDITS: the shared one on the leader when the
  // selected region is a follower, its own otherwise. Load-bearing, not cosmetic:
  // toggleTag computes the next keyword set from activeKeywords, so deriving those
  // from the follower's kept-but-ignored snapshot would clobber the leader's feed
  // on the first chip click. Writes route through effectiveTargetIds to match.
  const shared = allRegions
    ? (syncLeaderOf(selected, allRegions) ?? selected)
    : selected;
  const sharedIsLeader = shared !== selected;

  /**
   * Why auto-optimise is off, or null when nothing is overriding it.
   *
   * Names the facets rather than saying "some keywords": the switch disables itself in
   * response to a chip clicked further up the same panel, possibly minutes ago, and a
   * greyed-out control whose reason is elsewhere on screen is one the operator reads as
   * broken. The facet's own heading is reused so the sentence points at a group they can
   * see.
   */
  const optimizeOverrides = autoOptimizeOverrides(shared.source);
  const autoOptimizeOverride = optimizeOverrides.length
    ? `Off — this region picks its own ${optimizeOverrides
        .map((f) => facetLabel(f).toLowerCase())
        .join(" and ")}`
    : null;

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <RegionNameInput
          key={selected.id}
          name={selected.name ?? ""}
          placeholder={`Region ${index + 1}`}
          onRename={onRename}
        />
        {sharedIsLeader && allRegions ? (
          // The one line that stops the panel lying: everything below is the
          // LEADER's config, shown and edited from here \u2014 one feed, two tiles.
          <p className="rounded-md bg-[var(--color-panel-2)] px-2 py-1.5 text-xs text-zinc-300">
            Synced with{" "}
            <span className="font-medium">
              {shared.name?.trim() ||
                `Region ${allRegions.findIndex((r) => r.id === shared.id) + 1}`}
            </span>{" "}
            &mdash; showing its shared feed; edits here change both.
          </p>
        ) : null}
      </div>

      {/* The picker owns the loading spinner for both chip lists (they come from the same
          pair of fetches and arrive together). The Auto-optimise control below is NOT
          inside it: it needs no catalog data, and greying out a working control because
          something unrelated is in flight is its own small lie. The SHARED source goes in
          and the pick lands back on the leader via onSetSource → effectiveTargetIds. */}
      <FeedPicker
        collections={collections}
        tags={tags}
        loading={loading}
        source={shared.source}
        onSetSource={onSetSource}
        onCreateCollection={onCreateCollection}
      />

      {allRegions && onSetSync ? (
        <SyncControl
          selected={selected}
          allRegions={allRegions}
          onSetSync={onSetSync}
        />
      ) : null}

      {/* Duplicate / delete live in the toolbar under the canvas, next to add. */}
      <div className="space-y-2 border-t border-[var(--color-edge)] pt-3">
        {/* On its own row: the label cannot say what it does in the width left over next
            to a button, and this is the one control here whose effect is invisible until
            you know what it is for.

            `checked` is whether it is IN FORCE, not what is stored — a switch drawn on
            while a keyword overrides it would be claiming something untrue. The stored
            value is untouched, so clearing the keywords brings back whatever was chosen. */}
        <Toggle
          checked={autoOptimizeActive(shared)}
          disabled={autoOptimizeOverride !== null}
          onChange={onSetAutoOptimize}
          label="Auto-optimise"
          title={hintTitle(
            "Auto-optimise",
            autoOptimizeOverride ??
              "Fit clips and video quality to this region's size and shape. Off plays every clip at full quality, whatever its shape",
          )}
        />
        {/* Said on screen as well as in the title. A disabled control with its reason
            only on hover is a dead end on a phone, and this one disables itself in
            response to something the operator did three chips ago. */}
        {autoOptimizeOverride ? (
          <p className="px-1.5 text-xs text-zinc-500">{autoOptimizeOverride}</p>
        ) : null}
        {/* Sibling of the switch above, and for the same kind of reason: an effect the
            operator cannot see the name of. `contain` disables it because a fitted clip
            has no crop to steer — said on screen, not only on hover, for the reason
            given a few lines up. */}
        <Toggle
          checked={adaptiveFramingOn(shared)}
          disabled={shared.fit === "contain"}
          onChange={onSetAdaptiveFraming}
          label="Adaptive framing"
          title={hintTitle(
            "Adaptive framing",
            shared.fit === "contain"
              ? "Only applies where clips fill the region. This one fits them whole, so there is no crop to steer"
              : "Point the crop at what draws the eye rather than the middle of the frame. Holds a framing still, and moves only when the subject does",
          )}
        />
        {shared.fit === "contain" ? (
          <p className="px-1.5 text-xs text-zinc-500">
            Clips are fitted whole here, so nothing is cropped to steer.
          </p>
        ) : null}
        {/* A Muted toggle and a Random button stood beside this and are gone: sound is
            the SCREEN's now, not the region's, and "Random mix" is the default feed a
            region already starts on — a button restating the default explained nothing. */}
      </div>
    </div>
  );
}

/**
 * Aspect for the subject being edited. This used to also carry device kind, back
 * when the only subject was a Monitor (a cave-era concept); a View has none.
 */
function AspectSettings({
  aspect,
  onAspect,
}: {
  aspect: { w: number; h: number };
  onAspect: (a: { w: number; h: number }) => void;
}) {
  return (
    <div className="space-y-2">
      <label className="block">
        <span className="mb-1 block text-[11px] uppercase tracking-wide text-zinc-500">
          Aspect
        </span>
        <Select
          aria-label="Aspect ratio"
          value={aspectKey(aspect)}
          onChange={(e) => {
            const found = ASPECT_PRESETS.find(
              (a) => aspectKey(a) === e.target.value,
            );
            if (found) onAspect({ w: found.w, h: found.h });
          }}
          className="w-full py-1.5 text-xs"
        >
          {ASPECT_PRESETS.map((a) => (
            <option key={a.label} value={aspectKey(a)}>
              {a.label} — {a.hint}
            </option>
          ))}
          {/* If the stored aspect isn't one of the presets, keep it selectable. */}
          {ASPECT_PRESETS.some(
            (a) => aspectKey(a) === aspectKey(aspect),
          ) ? null : (
            <option value={aspectKey(aspect)}>{aspectKey(aspect)}</option>
          )}
        </Select>
      </label>

      <p className="text-[11px] text-zinc-500">
        Aspect sets the shape of the canvas and the layout previews.
      </p>
    </div>
  );
}

/* keywordSourceOf and the KeywordGroups facet picker live in FeedPicker.tsx now — the
   screen's region popup mounts the same picker, so the grouping rule (OR inside a facet,
   AND across facets) exists exactly once. */

/**
 * Sync this region to another one.
 *
 * The candidates are every OTHER region that is not itself a follower. Chains are refused
 * by `syncLeaderOf` at render time, so offering one here would let you build a layout that
 * silently renders black — better to not offer what cannot work.
 *
 * A synced pair is ONE feed with two tiles: the panel above shows and edits the
 * LEADER's config from either side (see `shared` in FeedPalette and
 * `effectiveTargetIds`), so nothing here is live-but-ignored any more. The region's
 * own source survives underneath as the unsync snapshot, which the notice says.
 */
function SyncControl({
  selected,
  allRegions,
  onSetSync,
}: {
  selected: Region;
  allRegions: Region[];
  onSetSync: (target: ID | null, mode: SyncMode) => void;
}) {
  const candidates = allRegions.filter((r) => r.id !== selected.id && !r.syncWith);
  const label = (r: Region) =>
    r.name?.trim() || `Region ${allRegions.findIndex((x) => x.id === r.id) + 1}`;
  const mode: SyncMode = selected.syncMode ?? "normal";
  const synced = !!selected.syncWith;

  // Nothing to sync TO. Said out loud rather than hidden, because a control that appears
  // only sometimes reads as a bug in a panel you are already learning.
  if (candidates.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-[var(--color-edge)] p-2.5">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
          Sync
        </div>
        <p className="mt-1 text-[11px] leading-relaxed text-zinc-600">
          Add another region to play the same clip in two places at once.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-[var(--color-edge)] p-2.5">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
          Sync
        </span>
        {synced ? (
          <button
            type="button"
            onClick={() => onSetSync(null, mode)}
            className="text-[11px] text-zinc-400 underline-offset-2 hover:text-zinc-200 hover:underline"
          >
            Unsync
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {candidates.map((r) => (
          <TagChip
            key={r.id}
            label={label(r)}
            active={selected.syncWith === r.id}
            // Clicking the region already synced to clears it, so the chip is a toggle
            // like every other chip in this panel rather than a one-way switch.
            onClick={() => onSetSync(selected.syncWith === r.id ? null : r.id, mode)}
          />
        ))}
      </div>

      {synced ? (
        <>
          <div className="mt-2 flex gap-1.5">
            {(["normal", "flip"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => onSetSync(selected.syncWith ?? null, m)}
                className={cx(
                  "flex-1 rounded-md border px-1.5 py-1.5 text-xs font-medium transition-colors",
                  mode === m
                    ? "border-transparent bg-[var(--color-accent)] text-white"
                    : "border-[var(--color-edge)] bg-[var(--color-panel-2)] text-zinc-300 hover:bg-[var(--color-edge)]",
                )}
              >
                {m === "normal" ? "Same" : "Mirrored"}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-[11px] leading-relaxed text-zinc-500">
            Plays the same clip, in step. Feed settings are shared — editing them from
            either region changes both. Unsyncing restores this region&rsquo;s own
            previous feed.
          </p>
        </>
      ) : null}
    </div>
  );
}

/** "More emoji" — the full grid popover at the end of the quick-pick row. Same
 *  outside-click/Escape dismissal every popover here uses. */
function EmojiPickerButton({
  current,
  onPick,
}: {
  current: string | undefined;
  onPick: (icon: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="More emoji"
        title="More emoji"
        onClick={() => setOpen((o) => !o)}
        className={cx(
          "h-9 w-9 rounded-lg border text-sm leading-none text-zinc-400",
          open
            ? "border-[var(--color-accent)]"
            : "border-[var(--color-edge)] hover:border-zinc-500",
        )}
      >
        …
      </button>
      {open ? (
        <div className="absolute left-0 top-full z-30 mt-1.5 max-h-72 w-72 overflow-y-auto rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel)] p-2 shadow-xl">
          {EMOJI_PICKER_GROUPS.map((g) => (
            <div key={g.label} className="mb-1.5">
              <p className="mb-1 text-[10px] uppercase tracking-wide text-zinc-500">
                {g.label}
              </p>
              <div className="flex flex-wrap gap-0.5">
                {g.emoji.map((e) => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => {
                      onPick(e);
                      setOpen(false);
                    }}
                    className={cx(
                      "h-8 w-8 rounded-md text-lg leading-none",
                      current === e
                        ? "bg-[var(--color-accent)]/20"
                        : "hover:bg-[var(--color-edge)]",
                    )}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The subject's own settings — what the left panel shows while no region is selected.
 * FeedPalette's view-level sibling: identity (name, card emoji) and whole-canvas
 * behaviour (layout, aspect, adaptive). Each identity control is gated on its
 * callback, so a host that owns that affordance elsewhere doesn't grow a second one.
 */
function ViewSettingsPanel({
  subject,
  onRenameView,
  onSetIcon,
  onAspect,
  onAdaptiveLayout,
  onPreset,
  onLockedLayout,
  maxRegions,
}: {
  subject: EditorSubject;
  onRenameView?: (name: string) => void;
  onSetIcon?: (icon: string | undefined) => void;
  onAspect: (aspect: { w: number; h: number }) => void;
  onAdaptiveLayout?: (on: boolean) => void;
  /** Apply a layout preset from the embedded mini grid; absent while the full
   *  selector is up in the Layout slot (two pickers at once would be noise). */
  onPreset?: (id: LayoutPresetId) => void;
  onLockedLayout?: () => void;
  maxRegions?: number;
}) {
  // Same commit rule as ViewHeader's rename: trim, keep a real change, revert a
  // blank — a nameless view renders as nothing everywhere its name shows.
  const [nameDraft, setNameDraft] = useState(subject.name);
  useEffect(() => setNameDraft(subject.name), [subject.name]);
  const commitName = () => {
    const name = nameDraft.trim();
    if (name && name !== subject.name) onRenameView?.(name);
    else setNameDraft(subject.name);
  };

  return (
    <div className="space-y-4">
      {onRenameView ? (
        <label className="block">
          <span className="mb-1 block text-[11px] uppercase tracking-wide text-zinc-500">
            Name
          </span>
          <TextInput
            value={nameDraft}
            maxLength={VIEW_NAME_MAX}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
              if (e.key === "Escape") setNameDraft(subject.name);
            }}
          />
        </label>
      ) : null}

      {onSetIcon ? (
        <div>
          <span className="mb-1 block text-[11px] uppercase tracking-wide text-zinc-500">
            Emoji
          </span>
          {/* One row, never wrapping: the current emoji leads (highlighted), the rest
              of the quick picks fill what's left of the line. A custom emoji that
              isn't a quick pick still shows up — first, like any current one. */}
          <div className="flex items-center gap-1.5">
            {[
              ...(subject.icon ? [subject.icon] : []),
              ...VIEW_EMOJI_CHOICES.filter((e) => e !== subject.icon),
            ]
              .slice(0, 11)
              .map((e) => (
              <button
                key={e}
                type="button"
                aria-pressed={subject.icon === e}
                title={e === subject.icon ? "Clear emoji" : "Use this emoji"}
                onClick={() => {
                  const clearing = subject.icon === e;
                  track("view_icon_set", clearing ? { via: "clear" } : { via: "quick", icon: e });
                  onSetIcon(clearing ? undefined : e);
                }}
                className={cx(
                  "h-9 w-9 rounded-lg border text-lg leading-none",
                  subject.icon === e
                    ? "border-[var(--color-accent)] bg-[var(--color-accent)]/15"
                    : "border-[var(--color-edge)] hover:border-zinc-500",
                )}
              >
                {e}
              </button>
            ))}
            <EmojiPickerButton
              current={subject.icon}
              onPick={(e) => {
                track("view_icon_set", { via: "picker", icon: e });
                onSetIcon(e);
              }}
            />
          </div>
          <p className="mt-1 text-[11px] text-zinc-500">
            Shown on the view&rsquo;s card.
          </p>
        </div>
      ) : null}

      {onPreset ? (
        <div>
          <span className="mb-1 block text-[11px] uppercase tracking-wide text-zinc-500">
            Layout
          </span>
          <PresetPalette
            onApply={onPreset}
            aspect={subject.aspect}
            maxRegions={maxRegions}
            onLocked={onLockedLayout}
            pageSize={8}
            cols={4}
          />
        </div>
      ) : null}

      <AspectSettings
        aspect={subject.aspect}
        onAspect={(a) => {
          // Preset labels only — a closed vocabulary, same rule as layout_picked.
          track("view_aspect_set", { aspect: `${a.w}:${a.h}` });
          onAspect(a);
        }}
      />

      {onAdaptiveLayout ? (
        <div className="space-y-1 border-t border-[var(--color-edge)] pt-3">
          <Toggle
            label="Adaptive layout"
            checked={subject.adaptiveLayout === true}
            onChange={(on) => {
              track("adaptive_layout_toggled", { on });
              onAdaptiveLayout(on);
            }}
          />
          <p className="text-[11px] text-zinc-500">
            On a portrait screen, columns become rows. Every region keeps
            its feed.
          </p>
        </div>
      ) : null}
    </div>
  );
}

/** One button in the toolbar under the canvas: icon-only on mobile (the title
 *  carries the name), icon + small text on desktop where the row has the width. */
function CanvasTool({
  label,
  onClick,
  disabled,
  danger,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={cx(
        "flex h-8 w-8 items-center justify-center rounded-md border border-[var(--color-edge)] bg-[var(--color-panel-2)] text-zinc-300 transition-colors",
        "lg:w-auto lg:gap-1.5 lg:px-2.5",
        "disabled:cursor-not-allowed disabled:opacity-35",
        danger
          ? "enabled:hover:border-red-500/60 enabled:hover:bg-red-500/15 enabled:hover:text-red-300"
          : "enabled:hover:border-[var(--color-accent)]/60 enabled:hover:text-zinc-100",
      )}
    >
      {children}
      <span className="hidden text-xs lg:inline">{label}</span>
    </button>
  );
}

/**
 * The active region's name — click to rename. Blank is fine: the canvas falls back
 * to "Region N", so that same fallback is what the unedited button shows.
 */
function RegionNameInput({
  name,
  placeholder,
  onRename,
}: {
  name: string;
  placeholder: string;
  onRename: (name: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);

  const commit = () => {
    const next = draft.trim();
    if (next !== name) onRename(next);
    setEditing(false);
  };

  if (editing) {
    return (
      <TextInput
        value={draft}
        autoFocus
        maxLength={40}
        placeholder={placeholder}
        aria-label="Region name"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") {
            setDraft(name);
            setEditing(false);
          }
        }}
        className="w-56"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title="Rename region"
      className="truncate rounded-md px-1.5 py-1 text-lg font-semibold text-zinc-100 transition-colors hover:bg-[var(--color-panel-2)]"
    >
      {name.trim() || placeholder}
    </button>
  );
}

function SectionLabel({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cx(
        "mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-400",
        className,
      )}
    >
      {children}
    </div>
  );
}

/* RegionLinkButton lives in its own file now, shared with the screen's hover chrome —
   see RegionLinkButton.tsx. */
